/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/MediaType.ts
 * @create:      2025-07-01 18:00:53.149
 * @modify:      2025-07-11 21:19:12.938
 * @version:     6.0.0
 * @times:       18
 * @lines:       90
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ArrayFirst } from "../array/ArrayFirst";
import { GetAllMediaTypes } from "../data/GetAllMediaTypes";
import { IMediaType } from "../interface/IMediaType";
import { IsEqual } from "../string/IsEqual";
import { TrimStart } from "../string/TrimStart";


/**
 * Retrieves an array of media types that match the specified name.
 *
 * @param name - The name of the media type to filter by. The comparison is case-insensitive.
 * @returns An array of `IMediaType` objects whose `type` property matches the provided name.
 * 
 * @since 6.0.0
 * @version 2025-07-04
 */
export function GetMediaTypes(name: string): IMediaType[] {
    const types = GetAllMediaTypes();
    return types.filter(p => IsEqual(p.type, name));
}

/**
 * Retrieves the first matching media type for the given name.
 *
 * @param name - The name of the media type to search for.
 * @returns The first `IMediaType` that matches the provided name, or `undefined` if no match is found.
 * 
 * @since 6.0.0
 * @version 2025-07-04
 */
export function GetMediaType(name: string): IMediaType | undefined {
    return ArrayFirst(GetMediaTypes(name));
}

/**
 * Retrieves an array of media types that match the given file extension.
 *
 * @param fileExtension - The file extension to search for (e.g., ".jpg", "png").
 * @returns An array of `IMediaType` objects whose extensions match the provided file extension.
 * 
 * @since 6.0.0
 * @version 2025-07-04
 */
export function GetMediaTypesByExtension(fileExtension: string): IMediaType[] {
    const types = GetAllMediaTypes();
    return types.filter(p => TrimStart(p.extension, ".", 1) === TrimStart(fileExtension, ".", 1).toLowerCase());
}

/**
 * Retrieves the first matching media type for a given file extension.
 *
 * @param fileExtension - The file extension (e.g., "jpg", "mp4") to look up.
 * @returns The first {@link IMediaType} associated with the extension, or `undefined` if none is found.
 */
export function GetMediaTypeByExtension(fileExtension: string): IMediaType | undefined {
    return ArrayFirst(GetMediaTypesByExtension(fileExtension));
}
